import { invoicesApi } from './invoicesApi';
import type {
  Invoice,
  InvoiceSearch,
  InvoiceStatus,
  InvoicePagedRequest,
  NextInvoiceNumberRequest,
  InvoiceFilterSelectData,
  InvoiceScheme,
  InvoiceSchemeInfo,
  AllowedTypesForScheme,
  InvoiceEnums,
  EnumOptionDto,
} from '../Types/invoicesTypes';
import type { InvoiceSearchV2ListItem, InvoiceSearchV2Request } from '../Types/invoiceSearchV2Types';
import { InvoiceType, VatRateType } from '../Types/invoicesTypes';
import { CorrectionDiff } from '../Types/correctionTypes';
import type { CreateInvoiceRequest } from '../Types/createInvoiceFormTypes';
import type {
  InvoicePaymentDto,
  RecordPaymentRequest,
  RecordPaymentResponse,
  RecordRefundRequest,
  RecordRefundResponse,
} from '../Types/paymentTypes';
import { TagTypes } from '../../../Api/Types/tagTypes';
import { cleanParams } from '../../Shared/Helpers/cleanParams';
import type { PagedList } from '../../Shared/Types/common';
import {
  buildCreateInvoiceRequest,
  buildLinkPrepaymentsRequest,
  buildReplacePrepaymentsRequest,
  buildUnlinkPrepaymentsRequest,
  buildUpdateInvoiceCommandRequest,
  type ReplacePrepaymentsArgs,
} from './invoiceRequestContracts';
import { invoiceLogger } from '../utils/invoiceLogger';
import {
  withSettlementBreakdownDefaults,
  withSettlementBreakdownDefaultsList,
} from '../utils/invoiceSettlementBreakdown';

interface CreateInvoiceCommandResponse {
  invoiceId: number;
}

export interface SettlementBreakdownTelemetryMetricsResult {
  fromUtc: string;
  toUtc: string;
  totalEvents: number;
  legacyFallbackAppliedCount: number;
  nonLegacyMissingBreakdownFallbackCount: number;
  blockedContractReadCount: number;
}

export interface SettlementBreakdownTelemetryEvent {
  eventType: string;
  observedAtUtc: string;
  invoiceId?: number;
  invoiceNumber?: string;
  isLegacySettlementRecord?: boolean;
  hasPaymentHistory?: boolean;
  missingFields: string[];
}

export interface SettlementBreakdownTelemetryEventsResult {
  fromUtc: string;
  toUtc: string;
  take: number;
  events: SettlementBreakdownTelemetryEvent[];
}

export const extendedInvoicesApi = invoicesApi.injectEndpoints({
  endpoints: (builder) => ({
    // Override default getAll to match backend route GET /api/admin/invoices/all
    getAll: builder.query<Invoice[], void>({
      query: () => '/invoices/all',
      transformResponse: (response: Invoice[]) => withSettlementBreakdownDefaultsList(response),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    getById: builder.query<Invoice, number>({
      query: (id) => `/invoices/${id}`,
      transformResponse: (response: Invoice) => withSettlementBreakdownDefaults(response),
      providesTags: (result, error, id) => [{ type: TagTypes.Invoice, id }],
    }),
    createInvoiceCommand: builder.mutation<CreateInvoiceCommandResponse, CreateInvoiceRequest>({
      query: (payload) => buildCreateInvoiceRequest(payload),
      invalidatesTags: [{ type: TagTypes.Invoice, id: TagTypes.List }],
    }),
    // Filters selects: GET /invoices/filters
    getInvoiceFilterSelects: builder.query<InvoiceFilterSelectData, void>({
      query: () => '/invoices/filters',
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    getSettlementBreakdownTelemetryMetrics: builder.query<
      SettlementBreakdownTelemetryMetricsResult,
      { fromUtc?: string; toUtc?: string } | void
    >({
      query: (params) => ({
        url: '/invoices/settlement-breakdown-telemetry/metrics',
        params:
          params && (params.fromUtc || params.toUtc)
            ? {
                ...(params.fromUtc ? { fromUtc: params.fromUtc } : {}),
                ...(params.toUtc ? { toUtc: params.toUtc } : {}),
              }
            : undefined,
      }),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    getSettlementBreakdownTelemetryEvents: builder.query<
      SettlementBreakdownTelemetryEventsResult,
      { fromUtc?: string; toUtc?: string; take?: number } | void
    >({
      query: (params) => ({
        url: '/invoices/settlement-breakdown-telemetry/events',
        params: {
          ...(params?.fromUtc ? { fromUtc: params.fromUtc } : {}),
          ...(params?.toUtc ? { toUtc: params.toUtc } : {}),
          ...(params?.take ? { take: params.take } : {}),
        },
      }),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    // Paged listing: GET /invoices/paged
    getInvoicesPaged: builder.query<PagedList<InvoiceSearch>, InvoicePagedRequest>({
      query: (params) => ({
        url: '/invoices/paged',
        params: cleanParams(params),
      }),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    // Search V2: GET /invoices/search-v2
    getInvoicesSearchV2: builder.query<PagedList<InvoiceSearchV2ListItem>, InvoiceSearchV2Request>({
      query: (params) => ({
        url: '/invoices/search-v2',
        params: cleanParams(params),
      }),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    // By reservation: GET /invoices/reservation/{reservationId}
    getInvoicesByReservation: builder.query<Invoice[], number>({
      query: (reservationId) => `/invoices/reservation/${reservationId}`,
      transformResponse: (response: Invoice[]) => withSettlementBreakdownDefaultsList(response),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    // By status: GET /invoices/status?status=...
    getInvoicesByStatus: builder.query<Invoice[], InvoiceStatus | string>({
      query: (status) => ({
        url: '/invoices/status',
        params: { status },
      }),
      transformResponse: (response: Invoice[]) => withSettlementBreakdownDefaultsList(response),
      providesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),
    // Next number preview: GET /invoices/next-number?type=...&companyId=...&issueDate=...
    getNextInvoiceNumber: builder.query<
      { number: string; isPreview: boolean; note: string },
      NextInvoiceNumberRequest
    >({
      query: ({ type, companyId, issueDate }) => ({
        url: '/invoices/next-number',
        params: { type, companyId, issueDate },
      }),
    }),
    // Get PDF: GET /invoices/{id}/pdf (Blob)
    getInvoicePdf: builder.query<Blob, number>({
      query: (id) => ({
        url: `/invoices/${id}/pdf`,
        method: 'GET',
        responseHandler: async (response) => {
          if (response.ok) {
            return await response.blob();
          }

          const contentType = response.headers.get('content-type') ?? '';
          const rawBody = await response.text();

          if (!rawBody) {
            return null;
          }

          const looksLikeJson =
            contentType.includes('application/json') ||
            contentType.includes('application/problem+json') ||
            rawBody.trim().startsWith('{') ||
            rawBody.trim().startsWith('[');

          if (!looksLikeJson) {
            return rawBody;
          }

          try {
            return JSON.parse(rawBody);
          } catch {
            return rawBody;
          }
        },
      }),
      providesTags: (result, error, id) => [{ type: TagTypes.Invoice, id }],
    }),
    // Regenerate PDF: POST /invoices/{id}/pdf/regenerate?templateVariant=...
    regenerateInvoicePdf: builder.mutation<
      { path: string },
      { id: number; templateVariant?: string }
    >({
      query: ({ id, templateVariant }) => ({
        url: `/invoices/${id}/pdf/regenerate`,
        method: 'POST',
        params: templateVariant ? { templateVariant } : undefined,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Invoice, id },
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),

    // Update invoice via CQRS command wrapper: PUT /invoices/{id}
    updateInvoiceCommand: builder.mutation<void, { id: number; invoice: Partial<Invoice> }>({
      query: (args) => buildUpdateInvoiceCommandRequest(args),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Invoice, id },
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),

    // Seller company by reservation: GET /invoices/reservations/{reservationId}/seller-company-id
    getSellerCompanyIdByReservation: builder.query<number, number>({
      query: (reservationId) => `/invoices/reservations/${reservationId}/seller-company-id`,
      transformResponse: (response: number, meta, reservationId) => {
        invoiceLogger.debug('[RTK Query] getSellerCompanyIdByReservation success', {
          reservationId,
          companyId: response,
          httpStatus: (meta as any)?.response?.status,
        });
        return response;
      },
      transformErrorResponse: (
        response: { status: number; data: unknown },
        meta,
        reservationId
      ) => {
        invoiceLogger.error('[RTK Query] getSellerCompanyIdByReservation error', {
          reservationId,
          status: response?.status,
          data: response?.data,
          httpStatus: (meta as any)?.response?.status,
        });
        return response;
      },
    }),

    // -------------------- SCHEMES / ENUMS --------------------
    // Schemes with PL labels: GET /invoices/schemes
    getInvoiceSchemes: builder.query<InvoiceSchemeInfo[], void>({
      query: () => '/invoices/schemes',
    }),

    // Allowed types for a given scheme (with labels): GET /invoices/schemes/{scheme}/types-with-labels
    getAllowedInvoiceTypesForSchemeWithLabels: builder.query<
      AllowedTypesForScheme,
      InvoiceScheme | number | string
    >({
      query: (scheme) => {
        invoiceLogger.debug(
          '[RTK Query] 🚀 getAllowedInvoiceTypesForSchemeWithLabels called with scheme:',
          scheme
        );
        return `/invoices/schemes/${scheme}/types-with-labels`;
      },
      transformResponse: (response: AllowedTypesForScheme, meta, arg) => {
        invoiceLogger.debug('[RTK Query] ✅ getAllowedInvoiceTypesForSchemeWithLabels response:', {
          scheme: arg,
          httpStatus: (meta as any)?.response?.status,
          allowedTypes: response?.allowedTypes?.map((t) => t.value) ?? [],
          count: response?.allowedTypes?.length ?? 0,
          rawResponse: response,
        });
        return response;
      },
      transformErrorResponse: (response: { status: number; data: unknown }, meta, arg) => {
        invoiceLogger.error('[RTK Query] ❌ getAllowedInvoiceTypesForSchemeWithLabels error:', {
          scheme: arg,
          status: response?.status,
          data: response?.data,
          httpStatus: (meta as any)?.response?.status,
        });
        return response;
      },
    }),

    // Combined enums endpoint: GET /invoices/enums
    getInvoiceEnums: builder.query<InvoiceEnums, void>({
      query: () => '/invoices/enums',
      providesTags: [{ type: TagTypes.Invoice, id: 'Enums' }],
    }),

    // Allowed schemes for a given type (with labels): GET /invoices/types/{type}/allowed-schemes-with-labels
    getAllowedSchemesForTypeWithLabels: builder.query<
      EnumOptionDto<InvoiceScheme>[],
      InvoiceType | number | string
    >({
      query: (type) => `/invoices/types/${type}/allowed-schemes-with-labels`,
    }),

    // Allowed VAT rates for a given scheme (with labels): GET /invoices/schemes/{scheme}/vat-rates-with-labels?type={type}
    // UPDATED: Added optional `type` parameter for context-specific prioritization (e.g., Refund -> VatNP first)
    getAllowedVatRatesForSchemeWithLabels: builder.query<
      EnumOptionDto<VatRateType>[],
      { scheme: InvoiceScheme | number | string; type?: InvoiceType | string }
    >({
      query: ({ scheme, type }) => ({
        url: `/invoices/schemes/${scheme}/vat-rates-with-labels`,
        params: type ? { type } : {},
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response)) return response;
        if (response && Array.isArray(response.allowedVatRates)) return response.allowedVatRates;
        if (response && Array.isArray(response.options)) return response.options;
        return [];
      },
      // Cache for 5 minutes (backend also caches for 1h)
      keepUnusedDataFor: 300,
    }),

    // -------------------- CALCULATIONS --------------------
    // Calculate invoice item: POST /invoices/items/calculate
    // NOT CACHED - always sends request for accurate calculation
    // Rate limited to 100 req/min per IP
    calculateInvoiceItem: builder.mutation<
      {
        unitNet: number;
        quantity: number;
        unitVat: number;
        totalNet: number;
        totalVat: number;
        unitGross: number;
        totalGross: number;
        vatType: VatRateType;
        vatRate: number;
      },
      {
        unitNet: number;
        quantity: number;
        vatType: VatRateType;
        vatRate: number;
      }
    >({
      query: (body) => ({
        url: '/invoices/items/calculate',
        method: 'POST',
        body,
      }),
      // No caching - always fresh calculation
      // No tags - doesn't affect other cached data
    }),

    // -------------------- PREPAYMENTS --------------------
    // Get prepayments by reservation: GET /invoices/reservation/{reservationId}/prepayments
    getPrepaymentsByReservation: builder.query<
      Invoice[],
      { reservationId: number; includeSettled?: boolean }
    >({
      query: ({ reservationId, includeSettled = false }) => ({
        url: `/invoices/reservation/${reservationId}/prepayments`,
        params: { includeSettled },
      }),
      transformResponse: (response: Invoice[]) => withSettlementBreakdownDefaultsList(response),
      providesTags: (result, error, { reservationId }) => [
        { type: TagTypes.Invoice, id: `PREPAYMENTS_${reservationId}` },
        { type: TagTypes.Invoice, id: 'List' },
      ],
    }),

    // Manually link prepayments to final invoice: POST /invoices/{finalInvoiceId}/link-prepayments
    linkPrepayments: builder.mutation<void, { finalInvoiceId: number; prepaymentIds: number[] }>({
      query: (args) => buildLinkPrepaymentsRequest(args),
      invalidatesTags: (result, error, { finalInvoiceId, prepaymentIds }) => {
        const tags: Array<{ type: TagTypes; id: string | number }> = [
          { type: TagTypes.Invoice, id: finalInvoiceId },
          { type: TagTypes.Invoice, id: 'List' },
        ];

        // Invalidate prepayments cache for all affected invoices
        prepaymentIds.forEach((id) => {
          tags.push({ type: TagTypes.Invoice, id });
        });

        return tags;
      },
    }),

    // Atomically replace the whole prepayment set: POST /invoices/{finalInvoiceId}/replace-prepayments
    replacePrepayments: builder.mutation<void, ReplacePrepaymentsArgs>({
      query: (args) => buildReplacePrepaymentsRequest(args),
      invalidatesTags: (
        result,
        error,
        { finalInvoiceId, prepaymentIds, previousPrepaymentIds }
      ) => {
        const tags: Array<{ type: TagTypes; id: string | number }> = [
          { type: TagTypes.Invoice, id: finalInvoiceId },
          { type: TagTypes.Invoice, id: 'List' },
        ];

        // Both the old and the new set are affected
        [...prepaymentIds, ...(previousPrepaymentIds ?? [])].forEach((id) => {
          tags.push({ type: TagTypes.Invoice, id });
        });

        return tags;
      },
    }),

    // Unlink prepayments from final invoice: POST /invoices/{finalInvoiceId}/unlink-prepayments
    unlinkPrepayments: builder.mutation<void, { finalInvoiceId: number; prepaymentIds?: number[] }>(
      {
        query: (args) => buildUnlinkPrepaymentsRequest(args),
        invalidatesTags: (result, error, { finalInvoiceId, prepaymentIds }) => {
          const tags: Array<{ type: TagTypes; id: string | number }> = [
            { type: TagTypes.Invoice, id: finalInvoiceId },
            { type: TagTypes.Invoice, id: 'List' },
          ];

          // Invalidate prepayments cache for all affected invoices
          if (prepaymentIds && prepaymentIds.length > 0) {
            prepaymentIds.forEach((id) => {
              tags.push({ type: TagTypes.Invoice, id });
            });
          }

          return tags;
        },
      }
    ),

    // -------------------- CORRECTION INVOICES --------------------
    /**
     * Get invoice for correction (with validation).
     * Validates if invoice can be corrected and returns its data.
     *
     * @param id - Base invoice ID
     * @throws 400 if invoice is Draft/Cancelled/Correction
     * @throws 404 if invoice not found
     */
    getInvoiceForCorrection: builder.query<Invoice, number>({
      query: (id) => {
        invoiceLogger.debug('[RTK Query] 🚀 getInvoiceForCorrection called with ID:', id);
        return `/invoices/${id}/for-correction`;
      },
      transformResponse: (response: Invoice, meta, arg) => {
        const normalizedResponse = withSettlementBreakdownDefaults(response);
        invoiceLogger.debug('[RTK Query] ✅ getInvoiceForCorrection response:', {
          invoiceId: normalizedResponse?.id,
          hasItems: !!normalizedResponse?.items,
          itemsCount: normalizedResponse?.items?.length || 0,
          scheme: normalizedResponse?.scheme,
          type: normalizedResponse?.type,
        });
        return normalizedResponse;
      },
      providesTags: (result, error, id) => [{ type: TagTypes.Invoice, id }],
    }),

    /**
     * Get list of corrections for an invoice.
     * Returns empty array if no corrections exist.
     *
     * @param id - Original invoice ID
     */
    getCorrections: builder.query<Invoice[], number>({
      query: (id) => `/invoices/${id}/corrections`,
      transformResponse: (response: Invoice[]) => withSettlementBreakdownDefaultsList(response),
      providesTags: (result, error, id) => [{ type: TagTypes.Invoice, id: `CORRECTIONS-${id}` }],
    }),

    /**
     * Get before/after diff for a correction invoice.
     *
     * @param id - Correction invoice ID (not base ID!)
     * @throws 400 if invoice is not a Correction type
     * @throws 404 if invoice or base invoice not found
     */
    getCorrectionDiff: builder.query<CorrectionDiff, number>({
      query: (id) => `/invoices/${id}/correction-diff`,
      providesTags: (result, error, id) => [{ type: TagTypes.Invoice, id: `DIFF-${id}` }],
    }),

    /**
    /**
     * Same endpoint as regular invoice creation, but with type=Correction.
     *
     * @param body - Invoice creation form data with correctionInvoiceId
     * @throws 400 if validation fails (scheme mismatch, etc.)
     */
    createCorrectionInvoice: builder.mutation<Invoice, any>({
      query: (body) => ({
        url: '/invoices',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Invoice, id: TagTypes.List },
        // Invalidate base invoice to refresh corrections list
        { type: TagTypes.Invoice, id: arg.correctionInvoiceId },
        // Invalidate corrections list for base invoice
        { type: TagTypes.Invoice, id: `CORRECTIONS-${arg.correctionInvoiceId}` },
      ],
    }),

    // ========================================
    // PAYMENT ENDPOINTS
    // ========================================

    /**
     * Get payment history for an invoice.
     * GET /api/admin/invoices/{invoiceId}/payments
     *
     * @param invoiceId - Invoice identifier
     * @returns List of payment records sorted by paymentDate DESC
     */
    getInvoicePaymentHistory: builder.query<InvoicePaymentDto[], number>({
      query: (invoiceId) => `/invoices/${invoiceId}/payments`,
      providesTags: (result, error, invoiceId) => [
        { type: TagTypes.Payment, id: invoiceId },
        { type: TagTypes.Invoice, id: invoiceId },
      ],
    }),

    /**
     * Record a payment for an invoice.
     * POST /api/admin/invoices/{invoiceId}/payments
     *
     * @param invoiceId - Invoice identifier
     * @param data - Payment details (amount, method, date, note)
     * @returns Created payment ID and linked reservation ID, when available
     */
    recordPayment: builder.mutation<
      RecordPaymentResponse,
      { invoiceId: number; data: RecordPaymentRequest }
    >({
      query: ({ invoiceId, data }) => ({
        url: `/invoices/${invoiceId}/payments`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: TagTypes.Payment, id: invoiceId },
        { type: TagTypes.Invoice, id: invoiceId },
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),

    /**
     * Record a refund for an invoice.
     * POST /api/admin/invoices/{invoiceId}/refunds
     *
     * @param invoiceId - Invoice identifier
     * @param data - Refund details (amount, method, date, note)
     * @returns Created refund ID and linked reservation ID, when available
     */
    recordRefund: builder.mutation<
      RecordRefundResponse,
      { invoiceId: number; data: RecordRefundRequest }
    >({
      query: ({ invoiceId, data }) => ({
        url: `/invoices/${invoiceId}/refunds`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: TagTypes.Payment, id: invoiceId },
        { type: TagTypes.Invoice, id: invoiceId },
        { type: TagTypes.Invoice, id: TagTypes.List },
      ],
    }),
  }),
  // Must be true to override existing endpoints from base API
  overrideExisting: true,
});

export const {
  useGetAllQuery: useGetInvoicesAllQuery,
  useGetByIdQuery: useGetInvoiceByIdQuery,
  useCreateInvoiceCommandMutation,
  useGetInvoicesPagedQuery,
  useGetInvoicesByReservationQuery,
  useLazyGetInvoicesByReservationQuery,
  useGetInvoicesByStatusQuery,
  useGetNextInvoiceNumberQuery,
  useGetInvoicePdfQuery,
  useLazyGetInvoicePdfQuery,
  useRegenerateInvoicePdfMutation,
  useUpdateInvoiceCommandMutation,
  useGetInvoiceFilterSelectsQuery,
  useGetSettlementBreakdownTelemetryMetricsQuery,
  useGetSettlementBreakdownTelemetryEventsQuery,
  useGetSellerCompanyIdByReservationQuery,
  useGetInvoiceSchemesQuery,
  useGetAllowedInvoiceTypesForSchemeWithLabelsQuery,
  useGetInvoiceEnumsQuery,
  useGetAllowedSchemesForTypeWithLabelsQuery,
  useGetAllowedVatRatesForSchemeWithLabelsQuery,
  useCalculateInvoiceItemMutation,
  useGetPrepaymentsByReservationQuery,
  useLinkPrepaymentsMutation,
  useReplacePrepaymentsMutation,
  useUnlinkPrepaymentsMutation,
  // Correction invoices hooks
  useGetInvoiceForCorrectionQuery,
  useGetCorrectionsQuery,
  useGetCorrectionDiffQuery,
  useCreateCorrectionInvoiceMutation,
  // Payment hooks
  useGetInvoicePaymentHistoryQuery,
  useRecordPaymentMutation,
  useRecordRefundMutation,
  useGetInvoicesSearchV2Query,
} = extendedInvoicesApi;
